<template>
    <div>
        <limitless-page-header>
            <template #header-elements>
                <limitless-page-header-button @click.prevent="showCreateProductForm" icon="icon-plus3">Ny produkt</limitless-page-header-button>
            </template>
        </limitless-page-header>

        <div class="content">
            <rednax-datatable
                ref="prodtable"
                :url="'/vue/products?mode=prodlist'"
                :columns="table_columns"
                :filter="table_filter"
                @updateFilter="updateFilter"
                history
                size="md"
                :per-page="per_page"
                sort-by="ProdID"
                sort-order="asc"
                :row-class="getRowClass"
                @rowclick="rowClick"
                hide-per-page
            >
                <div class="row">
                    <div class="col-lg-3 form-group">
                        <input type="text" v-model="table_filter.search" class="form-control" placeholder="Sök...">
                    </div>
                    <div class="col-lg-3 form-group">
                        <product-filter
                            v-model="table_filter.prod_id"
                            multiple
                            taggable
                            placeholder="SKU: 8602,8544..."
                            :reduce="(opt) => opt.ProdID || opt"
                        ></product-filter>
                    </div>
                    <div class="col-lg-3 form-group">
                        <supplier-filter
                            v-model="table_filter.supplier_id"
                            :reduce="(opt) => opt.id"
                            placeholder="-- Leverantör --"
                        />
                    </div>
                    <div class="col-lg-3 form-group">
                        <brand-filter
                            v-model="table_filter.brand_id"
                            :reduce="(opt) => opt.id"
                            placeholder="-- Varumärke --"
                        />
                    </div>
                    <div class="col-lg-3 form-group">
                        <card-serie-filter
                            v-model="table_filter.card_series_id"
                            placeholder="-- Finns på kortserie --"
                            :reduce="(opt) => opt.id"
                        ></card-serie-filter>
                    </div>
                    <div class="col-lg-3 form-group">
                        <category-filter
                            v-model="table_filter.category_id"
                            :reduce="(opt) => opt.id"
                            placeholder="-- Kategori --"
                        />
                    </div>
                    <div class="col-lg-3 form-group">
                        <category-sub-filter
                            v-model="table_filter.sub_category_id"
                            :reduce="(opt) => opt.id"
                            placeholder="-- Underkategori --"
                            :category-id="table_filter.category_id"
                        />
                    </div>
                    <div class="col-lg-3 form-group">
                        <v-select
                            v-model="table_filter.is_struct"
                            :options="status_options.map((opt) => ({ id: opt.id, label: opt.label + ' Strukturartiklar' }))"
                            placeholder="-- Strukturartiklar --"
                            :reduce="(opt) => opt.id"
                        ></v-select>
                    </div>
                    <div class="col-lg-3 form-group">
                        <v-select
                            v-model="table_filter.non_stock"
                            :options="status_options.map((opt) => ({ id: opt.id, label: opt.label + ' Beställningsvaror' }))"
                            placeholder="-- Beställningsvaror --"
                            :reduce="(opt) => opt.id"
                        ></v-select>
                    </div>
                    <div class="col-lg-3 form-group">
                        <v-select
                            v-model="table_filter.is_out_of_stock"
                            :options="status_options.map((opt) => ({ id: opt.id, label: opt.label + ' Tillfälligt slut' }))"
                            placeholder="-- Tillfälligt slut --"
                            :reduce="(opt) => opt.id"
                        ></v-select>
                    </div>
                    <div class="col-lg-3 form-group">
                        <v-select
                            v-model="table_filter.is_phased_out"
                            :options="status_options.map((opt) => ({ id: opt.id, label: opt.label + ' Utgått' }))"
                            placeholder="-- Utgått --"
                            :reduce="(opt) => opt.id"
                        ></v-select>
                    </div>
                    <div class="col-lg-3 form-group">
                        <v-select
                            v-model="table_filter.no_refill"
                            :options="status_options.map((opt) => ({ id: opt.id, label: opt.label + ' Ej Refill' }))"
                            placeholder="-- Ej Refill --"
                            :reduce="(opt) => opt.id"
                        ></v-select>
                    </div>
                    <div class="col-lg-3 form-group">
                        <v-select
                            v-model="table_filter.is_hidden_from_all_cards"
                            :options="status_options.map((opt) => ({ id: opt.id, label: opt.label + ' Gömd från alla kort' }))"
                            placeholder="-- Gömd från alla kort --"
                            :reduce="(opt) => opt.id"
                        ></v-select>
                    </div>
                    <div class="col-lg-3 form-group">
                        <vat-rate-filter
                            v-model="table_filter.vat_rate"
                            placeholder="-- Momssats --"
                            :reduce="(opt) => opt.id"
                        ></vat-rate-filter>
                    </div>
                    <div class="col-lg-3 form-group">
                        <min-max-slider-filter
                            :min-value="table_filter.np_min"
                            :max-value="table_filter.np_max"
                            @update:minValue="table_filter.np_min = $event"
                            @update:maxValue="table_filter.np_max = $event"
                            :min="0"
                            :max="9999"
                            label="Inköpspris"
                        ></min-max-slider-filter>
                    </div>
                    <div class="col-lg-6">
                        <div class="row">
                            <div class="col-lg-4 form-group">
                                <div class="custom-control custom-checkbox mb-1">
                                    <input type="checkbox" class="custom-control-input" id="chk-is_news" v-model="table_filter.is_news" :true-value="1" :false-value="null">
                                    <label id="embed-images" class="custom-control-label" for="chk-is_news">Är nyhet</label>
                                </div>
                                <div class="custom-control custom-checkbox">
                                    <input type="checkbox" class="custom-control-input" id="chk-np_incl_freight" v-model="table_filter.np_incl_freight" :true-value="1" :false-value="0">
                                    <label class="custom-control-label" for="chk-np_incl_freight">Filtrera inköpspris + frakt</label>
                                </div>
                            </div>
                            <div class="col-lg-4 form-group">
                                <div class="custom-control custom-checkbox mb-1">
                                    <input type="checkbox" class="custom-control-input" id="chk-has_purchase" v-model="table_filter.has_purchase" :true-value="1" :false-value="null">
                                    <label id="embed-images" class="custom-control-label" for="chk-has_purchase">Har kommande inköp</label>
                                </div>
                                <div class="custom-control custom-checkbox">
                                    <input type="checkbox" class="custom-control-input" id="chk-needs_purchase" v-model="table_filter.needs_purchase" :true-value="1" :false-value="null">
                                    <label id="embed-images" class="custom-control-label" for="chk-needs_purchase">Behöver köpas in</label>
                                </div>
                            </div>
                            <div class="col-lg-4 form-group">
                                <div class="custom-control custom-checkbox mb-1">
                                    <input type="checkbox" class="custom-control-input" id="chk-is_missing_category" v-model="table_filter.is_missing_category" :true-value="1" :false-value="null">
                                    <label class="custom-control-label" for="chk-is_missing_category">Saknar kategori</label>
                                </div>
                                <div class="custom-control custom-checkbox">
                                    <input type="checkbox" class="custom-control-input" id="chk-is_popular" v-model="table_filter.is_popular" :true-value="1" :false-value="null">
                                    <label class="custom-control-label" for="chk-is_popular">Är populär</label>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="columns.find((col) => col.data === 'redeemed').show"
                        class="col-lg-3 form-group"
                    >
                        <v-date-picker
                            v-model="date"
                            mode="date"
                            :locale="{ id: 'sv', firstDayOfWeek: 2, masks: { weekdays: 'WW', input: 'YYYY-MM-DD' } }"
                            :model-config="{ type: 'string', mask: 'YYYY-MM-DD' }"
                            is-range
                            show-iso-weeknumbers
                        >
                            <template v-slot="{ inputValue, togglePopover }">
                                <div class="input-group">
                                <span class="input-group-prepend">
                                    <span class="input-group-text">
                                        <i class="icon-calendar2"></i>
                                    </span>
                                </span>
                                    <input
                                        class="form-control"
                                        :value="(inputValue.start && inputValue.end ? inputValue.start + ' - ' + inputValue.end : '')"
                                        @focus="togglePopover()"
                                        placeholder="Datumintervall..."
                                    />
                                </div>
                            </template>
                        </v-date-picker>
                    </div>
                </div>
                <div class="row">
                    <div class="col-auto" style="margin-left: auto;">
                        <VDropdown>
                            <!-- This will be the popover target (for the events and position) -->
                            <button class="btn btn-light">
                                <i class="icon-hammer-wrench"></i>
                            </button>
                            <!-- This will be the content of the popover -->
                            <template #popper>
                                <div style="min-width: 250px;">
                                    <div class="list-group border-0">
                                        <a href="#" class="list-group-item list-group-item-action" @click.prevent="selectAll">
                                            <i class="icon-checkbox-checked mr-1"></i>Markera alla
                                        </a>
                                        <a href="#" class="list-group-item list-group-item-action" @click.prevent="deselectAll">
                                            <i class="icon-checkbox-unchecked mr-1"></i>Avmarkera alla
                                        </a>
                                        <a href="#" class="list-group-item list-group-item-action" @click.prevent="copySkusToClipboard">
                                            <i class="icon-copy4 mr-1"></i>Kopiera SKUs<span class="badge badge-primary badge-pill ml-1">{{ selected_products.length }}</span>
                                        </a>
                                        <a href="#" class="list-group-item list-group-item-action" @click.prevent="dispatchTranslateJob">
                                            <i v-if="busy_translate" class="icon-spinner2 spinner mr-1"></i>
                                            <i v-else class="icon-ligature mr-1"></i>
                                            Översätt med AI<span class="badge badge-primary badge-pill ml-1">{{ selected_products.length }}</span>
                                        </a>
                                        <a href="#" class="list-group-item list-group-item-action" @click.prevent="downloadExcel">
                                            <i v-if="busy_download" class="icon-spinner2 spinner mr-1"></i>
                                            <i v-else class="icon-file-excel mr-1"></i>
                                            Exportera till excel<span class="badge badge-primary badge-pill ml-1">{{ selected_products.length }}</span>
                                        </a>
                                    </div>
                                </div>
                            </template>
                        </VDropdown>
                    </div>
                    <div class="col-auto">
                        <VDropdown>
                            <!-- This will be the popover target (for the events and position) -->
                            <button class="btn btn-light">
                                <svg viewBox="0 0 16 16" width="1em" height="1em" focusable="false" role="img" aria-label="layout three columns" xmlns="http://www.w3.org/2000/svg" fill="currentColor" class="bi-layout-three-columns b-icon bi"><g><path d="M0 1.5A1.5 1.5 0 0 1 1.5 0h13A1.5 1.5 0 0 1 16 1.5v13a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 0 14.5v-13zM1.5 1a.5.5 0 0 0-.5.5v13a.5.5 0 0 0 .5.5H5V1H1.5zM10 15V1H6v14h4zm1 0h3.5a.5.5 0 0 0 .5-.5v-13a.5.5 0 0 0-.5-.5H11v14z"></path></g></svg>
                            </button>
                            <!-- This will be the content of the popover -->
                            <template #popper>
                                <div style="max-height: 300px; min-width: 250px; overflow-y: scroll; padding: 10px 10px 0 10px;">
                                    <div v-for="col in columns" class="custom-control custom-checkbox mb-2" :key="'col-' + col.data">
                                        <input type="checkbox" class="custom-control-input" :id="'col-' + col.data" v-model="col.show">
                                        <label class="custom-control-label" :for="'col-' + col.data">{{ col.header }}</label>
                                    </div>
                                </div>
                                <div class="p-1 border-top">
                                    <button type="button" class="btn btn-light btn-block" @click="saveSelectedColumns">Spara val</button>
                                </div>
                            </template>
                        </VDropdown>
                    </div>
                    <div class="col-auto" style="width: 160px;">
                        <v-select
                            v-model="per_page"
                            :reduce="(opt) => opt.id"
                            :options="[{ id: 25, label: '25 per sida' },{ id: 100, label: '100 per sida' },{ id: 250, label: '250 per sida' },{ id: 10000, label: 'Alla' }]"
                            :clearable="false"
                        ></v-select>
                    </div>
                </div>
                <template #cell_checkbox="{ row }">
                    <div class="custom-control custom-checkbox custom-control-inline">
                        <input :id="'chk-' + row.ProdID" type="checkbox" class="custom-control-input" v-model="selected_products" :value="row.ProdID">
                        <label class="custom-control-label" :for="'chk-' + row.ProdID" style="padding-left: 1.25rem;"></label>
                    </div>
                </template>
                <template #cell_ProdID="{ row }">
                    <router-link :to="{ name: 'products.edit', params: { id:  row.ProdID } }">{{ row.ProdID }}</router-link>
                </template>
                <template #cell_images="{ row }">
                    <div v-if="row.images && row.images.length > 0">
                        <img :id="`product-image-${row.ProdID}`" :src="row.images[0].urls['small']" :alt="row.ProdID" style="height: 60px; margin: -8px 0;">
                        <!--                    <div v-if="row.images.length > 1" style="margin-top: 8px; margin-bottom: -8px;">{{ row.images.length }} bilder i popup</div>-->
                        <b-popover :target="`product-image-${row.ProdID}`" triggers="hover">
                            <img :src="row.images[0].urls['medium']" :alt="row.ProdID" style="width: 100%;">
                        </b-popover>
                    </div>
                </template>
                <template #cell_isPhasedOut="{ row }">
                    <div v-if="row.isStruct" class="badge badge-info">STRUKTURARTIKEL</div>
                    <div v-if="row.isHiddenFromAllCards" class="badge badge-purple">GÖMD FRÅN KORT</div>
                    <div v-else-if="row.show_on_all_cards" class="badge badge-warning">VISAS PÅ ALLA KORT</div>
                    <div v-if="row.isPhasedOut === 1" class="badge badge-danger">HAR UTGÅTT</div>
                    <div v-else>
                        <div v-if="row.isNonStockItem" class="badge badge-warning">Beställningsvara</div>
                        <div v-if="row.isOutOfStock === 1">
                            <div class="badge badge-danger">TILLF. SLUT</div>
                            <div v-if="row.BackInStockDate && row.BackInStockDate != '0000-00-00'">Åter {{ row.BackInStockDate }}</div>
                            <div v-else-if="row.no_refill" class="badge badge-purple">EJ REFILL</div>
                            <div v-else-if="row.autoblockfunction === 'isPhasedOut'" class="badge badge-warning">UTGÅENDE</div>
                        </div>
                        <div v-else-if="row.no_refill" class="badge badge-purple">EJ REFILL</div>
                        <div v-else-if="row.autoblockfunction === 'isPhasedOut'" class="badge badge-warning">UTGÅENDE</div>
                    </div>
                </template>
                <template #cell_ReorderingPoint="{ row }">
                    <div class="text-right">{{ row.ReorderingPoint }}</div>
                </template>
                <template #cell_ProdNameWeb="{ row }">
                    {{ row.ProdNameWeb }}<span v-if="row.ProdQty || row.ProdVariant" v-html="[',', row.ProdQty, row.ProdVariant].filter((str) => str !== '').join(' ')"></span>
                </template>
                <template #cell_allowedDelivTypes="{ row }">
                    <div v-if="row.sendByExternalSupplier" class="badge badge-info mr-1">EXTERN LEV.</div>
                    <div v-else-if="row.sendByMail === 1" class="badge badge-success mr-1">VANLIGT BREV</div>
                    <div v-else-if="row.sendByMail === 2" class="badge badge-success mr-1">POCKET</div>
                    <div v-if="row.allowedDelivTypes === '' || (row.allowedDelivTypes & 1) === 1" class="badge badge-secondary mr-1">MP Home</div>
                    <div v-if="(row.allowedDelivTypes & 2) === 2" class="badge badge-secondary mr-1">MP Collect</div>
                    <div v-if="(row.allowedDelivTypes & 4) === 4" class="badge badge-secondary mr-1">ONLINE</div>
                    <div v-if="(row.allowedDelivTypes & 8) === 8" class="badge badge-secondary mr-1">VARUBREV</div>
                </template>
                <template #cell_order="{ row }">
                    {{ (row.Stock_today - row.Stock_future) }}
                </template>
                <template #cell_isNonStockItem="{ row }">
                    <div class="text-center">
                        <span v-if="row.isNonStockItem" class="icon-checkmark-circle text-green"></span>
                        <span v-else class="icon-cancel-circle2 text-danger"></span>
                    </div>
                </template>
                <template #cell_isStruct="{ row }">
                    <div class="text-center">
                        <span v-if="row.isStruct" class="icon-checkmark-circle text-green"></span>
                        <span v-else class="icon-cancel-circle2 text-danger"></span>
                    </div>
                </template>
                <template #cell_useStockCounts="{ row }">
                    <div class="text-center">
                        <span v-if="row.useStockCounts" class="icon-checkmark-circle text-green"></span>
                        <span v-else class="icon-cancel-circle2 text-danger"></span>
                    </div>
                </template>
                <template #cell_ordered_date="{ row }">
                    {{ (row.ordered_date ? row.ordered_date.substring(0, 10) : '') }}
                </template>
                <template #cell_LastRedeemedTS="{ row }">
                    {{ (row.LastRedeemedTS ? row.LastRedeemedTS.substring(0, 10) : '') }}
                </template>
                <template #cell_ProdVAT="{ row }">
                    {{ row.ProdVAT }} %
                </template>
                <template #cell_ProdNetPrice="{ row }">
                    {{ formatCurrency(row.ProdNetPrice) }}
                </template>
                <template #cell_ProdNetPriceInklFreight="{ row }">
                    {{ formatCurrency(row.ProdNetPriceInklFreight) }}
                </template>
                <template #cell_recommended_retail_price="{ row }">
                    {{ row.recommended_retail_price ? formatCurrency(row.recommended_retail_price) : '' }}
                </template>
                <template #cell_ProdWeight="{ row }">
                    {{ formatNumber(row.ProdWeight) }} kg
                </template>
                <template #cell_price_portal="{ row }">
                    {{ (row.price_portal > 0 ? formatCurrency(row.price_portal) : '') }}
                </template>
                <template #cell_allow_portal_addons="{ row }">
                    {{ (row.allow_portal_addons ? 'Ja' : 'Nej') }}
                </template>
                <template v-for="lang in languages" v-slot:[`cell_translations_ProdNameWebLang_${lang.short.toUpperCase()}`]="{ row }">
                    {{ renderTranslation(row, 'ProdNameWebLang', lang.short) }}
                </template>
            </rednax-datatable>
        </div>

        <b-modal
            id="modal_create_product"
            title="Ny produkt"
            key="modal_create_product"
            ok-title="Skapa"
            ok-variant="primary"
            @ok="createProduct"
            cancel-variant="light"
            cancel-title="Avbryt"
        >
            <ValidationObserver ref="productform">
                <form @submit.prevent="createProduct">
                    <ValidationProvider vid="ProdID" name="Produkt ID" rules="required" v-slot="{ errors }">
                        <div class="mb-2">
                            <label>Produkt ID <span class="text-danger">*</span></label>
                            <input type="text" class="form-control" v-model="product.ProdID" :class="{ 'border-danger':errors[0] }">
                            <span class="validation-invalid-label">{{ errors[0] }}</span>
                        </div>
                    </ValidationProvider>
                    <ValidationProvider vid="ProdName" name="Namn (Intern+Lapp)" :rules="{ required: !(product.NewportSku.length > 0) }" v-slot="{ errors }">
                        <div class="mb-2">
                            <label>Namn (Intern+Lapp) <span class="text-danger" v-if="!(product.NewportSku.length > 0)">*</span></label>
                            <input type="text" class="form-control" v-model="product.ProdName" :class="{ 'border-danger':errors[0] }">
                            <span class="validation-invalid-label">{{ errors[0] }}</span>
                        </div>
                    </ValidationProvider>
                    <ValidationProvider vid="Newport" name="Newport SKU" rules="" v-slot="{ errors }">
                        <div class="mb-2">
                            <label>Newport SKU</label>
                            <input type="text" class="form-control" v-model="product.NewportSku" :class="{ 'border-danger':errors[0] }">
                            <span class="validation-invalid-label" v-if="!!(errors[0])">{{ errors[0] }}</span>
                            <span class="text-muted" v-else>Använd EJ B2B SKU, då kommer prisimport bli fel.</span>
                        </div>
                    </ValidationProvider>
                </form>
            </ValidationObserver>
        </b-modal>
    </div>
</template>

<script>
import vSelect from 'vue-select';
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader';
import LimitlessPageHeaderButton from '@components/Layout/partials/LimitlessPageHeaderButton';
import RednaxDatatable from '@components/Datatable/RednaxDatatableComponent';
import { formatCurrency, formatNumber } from '@services/Helpers';
import apiClient from '@services/ApiClient';
import getError from '@services/ErrorHelper';
import Cookies from 'js-cookie';
import BackendRepository from '@services/repositories/BackendRepository';
import SupplierFilter from '@components/Filters/SupplierFilter.vue';
import BrandFilter from '@components/Filters/BrandFilter.vue';
import CategoryFilter from '@components/Filters/CategoryFilter.vue';
import MinMaxSliderFilter from '@components/Filters/MinMaxSliderFilter.vue';
import VatRateFilter from '@components/Filters/VatRateFilter.vue';
import VDatePicker from 'v-calendar/lib/components/date-picker.umd';
import CategorySubFilter from '@components/Filters/CategorySubFilter.vue';
import CardSerieFilter from '@components/Filters/CardSerieFilter.vue';
import ProductFilter from '@components/Filters/ProductFilter.vue';

export default {
    name: 'ProductList',
    components: {
        CardSerieFilter,
        CategorySubFilter,
        VDatePicker,
        VatRateFilter,
        MinMaxSliderFilter,
        CategoryFilter,
        BrandFilter,
        SupplierFilter,
        vSelect,
        LimitlessPageHeader,
        LimitlessPageHeaderButton,
        RednaxDatatable,
        ProductFilter,
    },
    data: () => ({
        busy_download: false,
        busy_translate: false,
        selected_products: [],
        date: null,
        filter_values: {
            min_prod_net_price: 0,
            max_prod_net_price: 9999,
            min_recommended_retail_price: 0,
            max_recommended_retail_price: 9999,
        },
        table_filter: {
            search: '',
            supplier_id: null,
            brand_id: null,
            card_series_id: null,
            category_id: null,
            sub_category_id: null,
            is_struct: null,
            is_out_of_stock: null,
            is_phased_out: 0,
            no_refill: null,
            is_hidden_from_all_cards: null,
            non_stock: null,
            is_news: null,
            np_incl_freight: 0,
            has_purchase: null,
            needs_purchase: null,
            is_missing_category: null,
            is_popular: null,
            vat_rate: null,
            np_min: null,
            np_max: null,
            prod_id: [],
            date_start: null,
            date_end: null,
        },
        per_page: 25,
        columns: [
            {
                header: '',
                data: 'checkbox',
                sortable: false,
                show: true,
                clickable: false,
                class: ['pr-0'],
            },
            {
                header: '#',
                data: 'ProdID',
                sortable: true,
                show: true,
            },
            {
                header: 'Bild',
                data: 'images',
                sortable: false,
                show: true,
            },
            {
                header: 'Namn (intern)',
                data: 'ProdName',
                sortable: true,
                show: true,
            },
            {
                header: 'Namn (web)',
                data: 'ProdNameWeb',
                sortable: true,
                show: false,
            },
            {
                header: 'Varumärke',
                data: 'brand_name',
                sortable: true,
                show: true,
            },
            {
                header: 'Leverantör',
                data: 'SupplierName',
                sortable: true,
                show: true,
            },
            {
                header: 'Lev. Artnr.',
                data: 'ProdSupplierSKU',
                sortable: true,
                show: true,
            },
            {
                header: 'Leveranssätt',
                data: 'allowedDelivTypes',
                sortable: false,
                show: false,
            },
            {
                header: 'Status',
                data: 'isPhasedOut',
                sortable: false,
                show: true,
            },
            {
                header: 'Lagersaldo',
                data: 'Stock_today',
                sortable: true,
                align_right: true,
                show: false,
            },
            {
                header: 'Reserverat',
                data: 'order',
                sortable: false,
                align_right: true,
                show: false,
            },
            {
                header: 'Disponibelt',
                data: 'Stock_future',
                sortable: true,
                align_right: true,
                show: false,
            },
            {
                header: 'Beställningsvara',
                data: 'isNonStockItem',
                sortable: true,
                align_right: false,
                show: false,
            },
            {
                header: 'Strukturartikel',
                data: 'isStruct',
                sortable: true,
                align_right: false,
                show: false,
            },
            {
                header: 'Lagersaldouträkning',
                data: 'useStockCounts',
                sortable: true,
                align_right: false,
                show: false,
            },
            {
                header: 'Best. punkt',
                data: 'ReorderingPoint',
                sortable: true,
                align_right: true,
                show: false,
                class(row) {
                    return (row.Stock_future <= row.ReorderingPoint ? 'bg-danger-300' : '');
                },
            },
            {
                header: 'Antal inlöst',
                data: 'redeemed',
                sortable: true,
                align_right: true,
                show: false,
            },
            {
                header: 'Senast inlöst',
                data: 'LastRedeemTS',
                sortable: true,
                show: false,
            },
            {
                header: 'Inkommande antal',
                data: 'ordered_total',
                sortable: true,
                align_right: true,
                show: false,
            },
            {
                header: 'Senaste best. datum',
                data: 'ordered_date',
                sortable: true,
                show: false,
            },
            {
                header: 'Moms',
                data: 'ProdVAT',
                sortable: true,
                align_right: true,
                show: false,
            },
            {
                header: 'Inköpspris',
                data: 'ProdNetPrice',
                sortable: true,
                align_right: true,
                show: false,
            },
            {
                header: 'Inköpspris inkl. frakt',
                data: 'ProdNetPriceInklFreight',
                sortable: true,
                align_right: true,
                show: false,
            },
            {
                header: 'Rek. förs.pris',
                data: 'recommended_retail_price',
                sortable: true,
                align_right: true,
                show: false,
            },
            {
                header: 'Ursprungsland',
                data: 'CustomsSourceCountryCode',
                sortable: true,
                show: false,
            },
            {
                header: 'Varukod',
                data: 'CustomsStatNo',
                sortable: true,
                show: false,
            },
            {
                header: 'Vikt',
                data: 'ProdWeight',
                sortable: true,
                align_right: true,
                show: false,
            },
            {
                header: 'Kategori',
                data: 'CategoryName',
                sortable: true,
                show: false,
            },
            {
                header: 'Underkategori',
                data: 'SubCategoryName',
                sortable: true,
                show: false,
            },
            {
                header: 'Nyhet t.o.m.',
                data: 'new_until',
                sortable: true,
                show: false,
            },
            {
                header: 'Är populär',
                data: 'is_popular',
                sortable: true,
                show: false,
            },
            {
                header: 'Portalpris',
                data: 'price_portal',
                sortable: true,
                show: false,
            },
            {
                header: 'Portalpoäng',
                data: 'points_portal',
                sortable: true,
                show: false,
            },
            {
                header: 'Portalköp',
                data: 'allow_portal_addons',
                sortable: true,
                show: false,
            },
        ],
        languages: [],
        product: {
            ProdID: '',
            ProdName: '',
            NewportSku: '',
        },
        status_options: [
            {
                id: null,
                label: 'Inkl. ',
            },
            {
                id: 1,
                label: 'Endast',
            },
            {
                id: 0,
                label: 'Dölj',
            },
        ],
    }),
    watch: {
        per_page(val) {
            Cookies.set('product_list_per_page', val, { expires: 365 });
        },
        date: {
            deep: true,
            handler(date) {
                if (date) {
                    // eslint-disable-next-line prefer-destructuring
                    this.table_filter.date_start = date.start;
                    // eslint-disable-next-line prefer-destructuring
                    this.table_filter.date_end = date.end;
                } else {
                    this.table_filter.date_start = null;
                    this.table_filter.date_end = null;
                }
            },
        },
        'table_filter.category_id': function watchProductProdCatId(newVal, oldVal) {
            if (oldVal !== undefined) {
                this.table_filter.sub_category_id = null;
            }
        },
    },
    mounted() {
        // this.getFilterValues();
        const end = new Date();
        const start = new Date();
        start.setFullYear(start.getFullYear() - 1);
        start.setMonth(10);
        start.setDate(1);
        this.date = {
            start: start.toISOString().substring(0, 10),
            end: end.toISOString().substring(0, 10),
        };

        const perPage = Cookies.get('product_list_per_page');
        if (perPage) {
            this.per_page = parseInt(perPage, 10);
        }
        BackendRepository.getLanguages().then((res) => {
            this.languages = res;
            res.forEach((lang) => {
                if (lang.short.toLowerCase() !== 'sv') {
                    this.columns.push({
                        header: 'Namn (web) (' + lang.short.toLowerCase() + ')',
                        data: 'translations_ProdNameWebLang_' + lang.short.toUpperCase(),
                        sortable: false,
                        show: false,
                    });
                }
            });
        }).then(() => {
            const colState = Cookies.get('product_list_columns');
            if (colState) {
                Object.entries(JSON.parse(colState)).forEach(([key, val]) => {
                    const index = this.columns.findIndex((col) => col.data === key);
                    if (index !== false) {
                        this.columns[index].show = val;
                    }
                });
            }
        });
    },
    methods: {
        formatCurrency,
        formatNumber,
        downloadExcel() {
            this.busy_download = true;
            apiClient.post('/vue/products/tools/export', { prod_ids: this.selected_products })
                .then((response) => {
                    // Create a link and set the URL using `createObjectURL`
                    const link = document.createElement('a');
                    link.style.display = 'none';
                    link.href = response.data.url;
                    link.download = '';

                    // It needs to be added to the DOM so it can be clicked
                    document.body.appendChild(link);
                    link.click();

                    // To make this work on Firefox we need to wait
                    // a little while before removing it.
                    setTimeout(() => {
                        URL.revokeObjectURL(link.href);
                        link.parentNode.removeChild(link);
                    }, 0);
                })
                .finally(() => {
                    this.busy_download = false;
                });
        },
        getFilterValues() {
            apiClient.post('/vue/products/filter_values', this.table_filter)
                .then((response) => {
                    this.filter_values.min_prod_net_price = response.data.min_prod_net_price ?? 0;
                    this.filter_values.max_prod_net_price = response.data.max_prod_net_price ?? 9999;
                    this.filter_values.min_recommended_retail_price = response.data.min_recommended_retail_price ?? 0;
                    this.filter_values.max_recommended_retail_price = response.data.max_recommended_retail_price ?? 9999;
                })
                .catch(() => {
                    this.filter_values.min_prod_net_price = 0;
                    this.filter_values.max_prod_net_price = 9999;
                    this.filter_values.min_recommended_retail_price = 0;
                    this.filter_values.max_recommended_retail_price = 9999;
                });
        },
        updateFilter(filter) {
            this.table_filter = filter;
            // this.getFilterValues();
        },
        dispatchTranslateJob() {
            this.busy_translate = true;
            apiClient.post('/vue/products/tools/translate', { prod_ids: this.selected_products })
                .then((response) => {
                    this.$swal({
                        title: response.data.message,
                        icon: 'success',
                        showCancelButton: false,
                        confirmButtonText: 'Ok',
                        timer: 2000,
                    });
                })
                .finally(() => {
                    this.busy_translate = false;
                });
        },
        selectAll() {
            if (this.$refs.prodtable && this.$refs.prodtable.local_data && Array.isArray(this.$refs.prodtable.local_data.prod_ids)) {
                this.selected_products = [...this.$refs.prodtable.local_data.prod_ids];
            }
        },
        deselectAll() {
            this.selected_products = [];
        },
        copySkusToClipboard() {
            navigator.clipboard.writeText(this.selected_products.join(','));
        },
        getRowClass(row) {
            const arr = [];
            if (row.isPhasedOut === 1) {
                arr.push('bg-danger-200');
                arr.push('text-line-through');
            } else if (row.isOutOfStock === 1) {
                arr.push('bg-yellow-200');
            }
            if (row.isStruct === 1) {
                arr.push('font-weight-bold');
            }
            return arr;
        },
        rowClick(data, event) {
            if (event.metaKey === true || event.ctrlKey === true) {
                const route = this.$router.resolve({ name: 'products.edit', params: { id: data.ProdID } });
                window.open(route.href, '_blank').focus();
            } else {
                this.$router.push({ name: 'products.edit', params: { id: data.ProdID } });
            }
        },
        showCreateProductForm() {
            this.getNextProdID();
            this.$root.$bvModal.show('modal_create_product');
        },
        async getNextProdID() {
            await apiClient.post('/vue/products/next')
                .then((response) => {
                    this.product.ProdID = response.data.ProdID;
                })
                .catch(() => {
                    this.product.ProdID = '';
                });
        },
        async createProduct(e) {
            e.preventDefault();
            const isValid = await this.$refs.productform.validate();
            if (isValid) {
                this.busy = true;
                await apiClient.post('/vue/products/new', this.product)
                    .then((response) => {
                        window.location.href = '/products/' + response.data.product.ProdID;
                        this.$root.$bvModal.hide('modal_create_product');
                    })
                    .catch((error) => {
                        const parsederror = getError(error);
                        if (typeof parsederror === 'object') {
                            this.$refs.productform.setErrors(parsederror);
                        }
                    });
            }
        },
        renderTranslation(row, propery, langcode) {
            const translation = row.translations.find((trans) => trans.LangCode.toUpperCase() === langcode.toUpperCase());
            if (translation && translation[propery]) {
                return translation[propery] + ((propery === 'ProdNameWebLang' && (row.ProdQty || row.ProdVariant)) ? [',', row.ProdQty, translation.ProdVariantLang].filter((str) => str !== '').join(' ') : '');
            }
            return '';
        },
        saveSelectedColumns() {
            const colState = {};
            this.columns.forEach((col) => {
                colState[col.data] = col.show;
            });
            Cookies.set('product_list_columns', JSON.stringify(colState), { expires: 365 });
        },
    },
    computed: {
        table_columns() {
            return this.columns.filter((item) => item.show);
        },
    },
};

</script>
